/**
 * Live Captions Studio — near-real-time transcription using MediaRecorder + Saaras v4.
 *
 * How it works:
 *   • Uses the browser MediaRecorder API to capture microphone audio.
 *   • Every ~12 seconds, we stop the recorder, POST the accumulated blob to
 *     /api/sarvam/stt (translit/transcribe/translate — user choice), and
 *     immediately restart a fresh recorder for the next chunk.
 *   • Chunk transcripts stream into an on-screen caption panel with
 *     timestamps + copy/export controls.
 *
 * Not full-WebSocket real-time — but zero backend changes needed and gives
 * users a live-feeling captions experience for lectures, podcasts, live
 * streams etc.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mic,
  MicOff,
  Loader2,
  Download,
  Copy,
  Trash2,
  Circle,
  Languages as LanguagesIcon,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/sarvam`;

const CHUNK_MS = 12000; // 12s chunks — well under Sarvam sync 30s cap.

const LANGS = [
  { code: "auto", label: "Auto-detect" },
  { code: "hi", label: "Hindi" },
  { code: "hinglish", label: "Hinglish (Roman)" },
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "bn", label: "Bengali" },
  { code: "gu", label: "Gujarati" },
  { code: "mr", label: "Marathi" },
  { code: "kn", label: "Kannada" },
  { code: "ml", label: "Malayalam" },
  { code: "pa", label: "Punjabi" },
  { code: "or", label: "Odia" },
];

const MODES = [
  { code: "transcribe", label: "Native script (transcribe)" },
  { code: "translit", label: "Roman Hinglish (translit)" },
  { code: "translate", label: "Translate → English" },
];

function fmtTime(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export default function LiveCaptionsPage() {
  const [recording, setRecording] = useState(false);
  const [chunks, setChunks] = useState([]); // [{start_ms, text, lang, busy?}]
  const [language, setLanguage] = useState("hi");
  const [mode, setMode] = useState("translit");
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const startedAtRef = useRef(0);
  const chunkStartRef = useRef(0);
  const cycleTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const stopRequestedRef = useRef(false);

  // Timer tick
  useEffect(() => {
    if (recording) {
      tickTimerRef.current = setInterval(() => {
        setElapsed(Date.now() - startedAtRef.current);
      }, 500);
    }
    return () => { if (tickTimerRef.current) clearInterval(tickTimerRef.current); };
  }, [recording]);

  const cleanup = () => {
    if (cycleTimerRef.current) { clearTimeout(cycleTimerRef.current); cycleTimerRef.current = null; }
    if (tickTimerRef.current) { clearInterval(tickTimerRef.current); tickTimerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
  };

  const uploadChunk = async (blob, startMs) => {
    // Add placeholder immediately so the panel shows progress
    const placeholderId = `${startMs}-${Math.random()}`;
    setChunks(prev => [...prev, { id: placeholderId, start_ms: startMs, text: "", lang: language, busy: true }]);

    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : (blob.type.includes("mp4") ? "mp4" : "wav");
      fd.append("file", blob, `chunk-${startMs}.${ext}`);
      fd.append("mode", mode);
      fd.append("language", language);
      const res = await axios.post(`${API}/stt`, fd, { timeout: 60000 });
      const result = res.data?.result || {};
      setChunks(prev => prev.map(c => c.id === placeholderId
        ? { ...c, text: (result.text || "").trim(), lang: result.language || language, busy: false }
        : c
      ));
    } catch (e) {
      setChunks(prev => prev.map(c => c.id === placeholderId
        ? { ...c, text: "(failed to transcribe)", busy: false, error: true }
        : c
      ));
    }
  };

  const startCycle = (stream) => {
    // Build a fresh MediaRecorder that runs for CHUNK_MS, then dumps and restarts.
    stopRequestedRef.current = false;
    const runOne = () => {
      if (stopRequestedRef.current) return;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "audio/webm");
      const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128000 });
      recorderRef.current = rec;
      const pieces = [];
      const chunkStartedAt = Date.now() - startedAtRef.current;
      chunkStartRef.current = chunkStartedAt;

      rec.ondataavailable = (e) => { if (e.data && e.data.size) pieces.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(pieces, { type: mime });
        if (blob.size > 4000) uploadChunk(blob, chunkStartedAt);
        if (!stopRequestedRef.current) runOne();
      };
      rec.start();
      cycleTimerRef.current = setTimeout(() => {
        if (rec.state === "recording") rec.stop();
      }, CHUNK_MS);
    };
    runOne();
  };

  const startRecording = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser doesn't support live audio capture.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      startedAtRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      startCycle(stream);
      toast.success("Recording — captions will stream every ~12 s");
    } catch (e) {
      const friendly = e?.name === "NotAllowedError"
        ? "Microphone access blocked. Please allow mic access in your browser and try again."
        : (e?.message || "Could not start recording.");
      setError(friendly);
      cleanup();
    }
  };

  const stopRecording = () => {
    stopRequestedRef.current = true;
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    setRecording(false);
    cleanup();
    toast.success("Stopped");
  };

  useEffect(() => () => { stopRequestedRef.current = true; cleanup(); }, []);

  const fullTranscript = chunks.map(c => c.text).filter(Boolean).join(" ");

  const copyAll = () => {
    navigator.clipboard.writeText(fullTranscript);
    toast.success("Transcript copied!");
  };

  const downloadSrt = () => {
    const srt = chunks.filter(c => c.text && !c.error).map((c, i) => {
      const start = c.start_ms;
      const end = start + CHUNK_MS;
      const fmt = (ms) => {
        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        const mils = ms % 1000;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")},${String(mils).padStart(3, "0")}`;
      };
      return `${i + 1}\n${fmt(start)} --> ${fmt(end)}\n${c.text}\n`;
    }).join("\n");
    const blob = new Blob([srt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "live-captions.srt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-orange-100 text-orange-800 border-transparent">🇮🇳 MarkeeMix Indic AI</Badge>
            <Badge className="bg-red-100 text-red-800 border-transparent animate-pulse">LIVE</Badge>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            Live Captions Studio
          </h1>
          <p className="text-neutral-600 mt-3 max-w-2xl text-base">
            Speak into your mic — get streaming Hindi / Tamil / Telugu / Bengali / Marathi captions
            within ~12 seconds of each phrase. Perfect for live lectures, podcast dry-runs,
            interviews and streaming.
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-10">
        {/* Controls */}
        <Card className="p-6 border-neutral-200 mb-6">
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <label className="text-xs font-semibold text-neutral-600">
              Source language
              <select value={language} onChange={(e) => setLanguage(e.target.value)} disabled={recording}
                className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm disabled:bg-neutral-100"
                data-testid="live-language">
                {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-neutral-600">
              Output style
              <select value={mode} onChange={(e) => setMode(e.target.value)} disabled={recording}
                className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm disabled:bg-neutral-100"
                data-testid="live-mode">
                {MODES.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </label>
            {recording ? (
              <Button onClick={stopRecording} className="h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="live-stop">
                <MicOff className="w-4 h-4 mr-2" /> Stop
              </Button>
            ) : (
              <Button onClick={startRecording} className="h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="live-start">
                <Mic className="w-4 h-4 mr-2" /> Start recording
              </Button>
            )}
          </div>
          {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
          {recording && (
            <div className="mt-4 flex items-center gap-3 text-sm">
              <Circle className="w-3 h-3 fill-red-600 text-red-600 animate-pulse" />
              <span className="font-mono text-neutral-700 tabular-nums" data-testid="live-elapsed">{fmtTime(elapsed)}</span>
              <span className="text-neutral-500">Streaming captions every ~12s…</span>
            </div>
          )}
        </Card>

        {/* Captions panel */}
        <Card className="p-6 border-neutral-200 min-h-[360px]" data-testid="live-captions-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-[#111111] flex items-center gap-2">
              <LanguagesIcon className="w-4 h-4 text-[#A855F7]" /> Live transcript
              {chunks.length > 0 && <Badge variant="secondary">{chunks.filter(c => c.text).length} segments</Badge>}
            </h2>
            {chunks.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyAll} data-testid="live-copy">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
                <Button variant="outline" size="sm" onClick={downloadSrt} data-testid="live-download-srt">
                  <Download className="w-3 h-3 mr-1" /> Save .srt
                </Button>
                <Button variant="outline" size="sm" onClick={() => setChunks([])} disabled={recording} data-testid="live-clear">
                  <Trash2 className="w-3 h-3 mr-1" /> Clear
                </Button>
              </div>
            )}
          </div>

          {chunks.length === 0 ? (
            <div className="text-center py-16 text-neutral-400">
              <Mic className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Click <span className="font-semibold">Start recording</span> to begin.</p>
              <p className="text-xs mt-1">Captions will appear here every ~12 seconds.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {chunks.map((c) => (
                <div key={c.id} className={`flex gap-4 p-3 rounded-lg ${c.busy ? "bg-neutral-50" : c.error ? "bg-red-50" : "bg-white border border-neutral-100"}`}>
                  <span className="font-mono text-xs text-neutral-500 tabular-nums whitespace-nowrap mt-0.5">{fmtTime(c.start_ms)}</span>
                  <div className="flex-1">
                    {c.busy ? (
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
                      </div>
                    ) : (
                      <p className={`text-sm leading-relaxed ${c.error ? "text-red-700" : "text-neutral-800"}`}>{c.text || "(silence)"}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <p className="mt-6 text-center text-xs text-neutral-500">
          Near-real-time: audio streams in 12-second chunks. For true &lt;1 s latency, we&apos;ll enable
          our realtime WebSocket engine in a future update.
        </p>
      </main>

      <PremiumFooter />
    </div>
  );
}
